import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { inspectionsApi, emailApi } from '../lib/api';
import { 
  formatDateTime, 
  getStatusColor, 
  getStatusLabel, 
  getInspectionStatusColor, 
  getInspectionStatusLabel,
  calculateProgress 
} from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { SignatureSection } from '../components/SignaturePad';
import { MittlereHoeheForm } from '../components/MittlereHoeheForm';
import { 
  ArrowLeft, 
  Loader2, 
  CheckCircle2, 
  XCircle, 
  MinusCircle,
  Camera,
  Trash2,
  FileText,
  MapPin,
  User,
  Building2,
  Calendar,
  Download,
  AlertTriangle,
  AlertCircle,
  MessageSquare,
  Image as ImageIcon,
  HelpCircle,
  BookOpen,
  ChevronDown,
  ChevronUp,
  Scale,
  ListChecks,
  Lock,
  CheckCheck,
  PenLine,
  Mail,
  Send,
  History
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';

export default function InspectionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inspection, setInspection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [expandedHelp, setExpandedHelp] = useState({});
  const [selectedItem, setSelectedItem] = useState(null);
  const [comment, setComment] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState('both');
  const [customEmail, setCustomEmail] = useState('');
  const [includePdf, setIncludePdf] = useState(true);
  const [emailConfigured, setEmailConfigured] = useState(false);
  const [showEmailLogDialog, setShowEmailLogDialog] = useState(false);
  const [emailLogs, setEmailLogs] = useState([]);
  const [loadingEmailLog, setLoadingEmailLog] = useState(false);
  const [emailResultsDialog, setEmailResultsDialog] = useState(null); // { results: [...], allSuccess: bool }
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchInspection();
    checkEmailStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const checkEmailStatus = async () => {
    try {
      const response = await emailApi.getStatus();
      setEmailConfigured(response.data.configured);
    } catch (error) {
      console.log('Email status check failed:', error);
    }
  };

  const fetchInspection = async () => {
    try {
      const response = await inspectionsApi.getById(id);
      setInspection(response.data);
    } catch (error) {
      console.error('Error fetching inspection:', error);
      navigate('/inspections');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (itemId, newStatus) => {
    setUpdating(itemId);
    try {
      const item = inspection.items.find(i => i.id === itemId);
      await inspectionsApi.updateItem(id, itemId, {
        status: newStatus,
        comment: item?.comment || ''
      });
      await fetchInspection();
    } catch (error) {
      console.error('Error updating item:', error);
    } finally {
      setUpdating(null);
    }
  };

  const handleCommentSave = async () => {
    if (!selectedItem) return;
    
    setUpdating(selectedItem.id);
    try {
      await inspectionsApi.updateItem(id, selectedItem.id, {
        status: selectedItem.status,
        comment: comment
      });
      await fetchInspection();
      setSelectedItem(null);
      setComment('');
    } catch (error) {
      console.error('Error saving comment:', error);
    } finally {
      setUpdating(null);
    }
  };

  // Massnahmen-Farbfunktionen
  const getMassnahmeColor = (typ) => {
    const colors = {
      'sofort': 'rgba(254, 202, 202, 0.5)',      // Rot
      'kurzfristig': 'rgba(254, 243, 199, 0.5)', // Gelb
      'mittelfristig': 'rgba(209, 250, 229, 0.5)', // Grün
      'langfristig': 'rgba(191, 219, 254, 0.5)', // Blau
    };
    return colors[typ] || 'rgba(243, 244, 246, 0.5)';
  };

  const getMassnahmeBorderColor = (typ) => {
    const colors = {
      'sofort': 'rgb(239, 68, 68)',        // Rot
      'kurzfristig': 'rgb(245, 158, 11)',  // Gelb/Orange
      'mittelfristig': 'rgb(34, 197, 94)', // Grün
      'langfristig': 'rgb(59, 130, 246)',  // Blau
    };
    return colors[typ] || 'rgb(209, 213, 219)';
  };

  // Massnahmen-Handler
  const handleMassnahmeChange = async (itemId, massnahmeTyp) => {
    setUpdating(itemId);
    try {
      const item = inspection.items.find(i => i.id === itemId);
      await inspectionsApi.updateItem(id, itemId, {
        status: item?.status || 'fail',
        comment: item?.comment || '',
        massnahme_typ: massnahmeTyp,
        massnahme_text: item?.massnahme_text || ''
      });
      
      // Synchronisiere Massnahme in mittlere_hoehe_extras (falls "Mittlere Höhe" Checkliste)
      if (inspection.checklist_id === 'mittlere-hoehe-11-30m') {
        await syncMassnahmeToExtras(itemId, item, massnahmeTyp, item?.massnahme_text || '');
      }
      
      await fetchInspection();
    } catch (error) {
      console.error('Error updating massnahme:', error);
    } finally {
      setUpdating(null);
    }
  };

  // Synchronisiert Massnahmen aus Prüfpunkten in mittlere_hoehe_extras.massnahmen
  const syncMassnahmeToExtras = async (itemId, item, massnahmeTyp, massnahmeText) => {
    try {
      // Hole aktuelle Extras - API gibt { mittlere_hoehe_extras: {...} } zurück
      const extrasResponse = await inspectionsApi.getMittlereHoeheExtras(id);
      const currentExtras = extrasResponse.data?.mittlere_hoehe_extras || {};
      const massnahmen = [...(currentExtras.massnahmen || [])];
      
      // Finde existierende Massnahme für diesen Prüfpunkt
      const existingIndex = massnahmen.findIndex(m => m.checklist_item_id === itemId);
      
      if (massnahmeTyp) {
        // Erstelle/Aktualisiere Massnahme
        const newMassnahme = {
          id: existingIndex >= 0 ? massnahmen[existingIndex].id : `massnahme-${Date.now()}`,
          beschreibung: massnahmeText || `${item?.name || 'Prüfpunkt'}: Mangel festgestellt`,
          frist: getFristFromKategorie(massnahmeTyp),
          kategorie: massnahmeTyp,
          erledigt: false,
          checklist_item_id: itemId
        };
        
        if (existingIndex >= 0) {
          massnahmen[existingIndex] = newMassnahme;
        } else {
          massnahmen.push(newMassnahme);
        }
      } else if (existingIndex >= 0) {
        // Entferne Massnahme wenn Typ gelöscht wird
        massnahmen.splice(existingIndex, 1);
      }
      
      // Speichere aktualisierte Extras
      await inspectionsApi.updateMittlereHoeheExtras(id, {
        ...currentExtras,
        massnahmen
      });
    } catch (error) {
      console.error('Error syncing massnahme to extras:', error);
    }
  };

  // Hilfsfunktion: Frist basierend auf Kategorie berechnen
  const getFristFromKategorie = (kategorie) => {
    const heute = new Date();
    switch (kategorie) {
      case 'sofort':
        return heute.toISOString().split('T')[0]; // Heute
      case 'kurzfristig':
        heute.setMonth(heute.getMonth() + 6);
        return heute.toISOString().split('T')[0]; // 6 Monate
      case 'mittelfristig':
        heute.setMonth(heute.getMonth() + 18);
        return heute.toISOString().split('T')[0]; // 18 Monate
      case 'langfristig':
        heute.setFullYear(heute.getFullYear() + 3);
        return heute.toISOString().split('T')[0]; // 3 Jahre
      default:
        return '';
    }
  };

  const handleMassnahmeTextChange = async (itemId, text) => {
    // Debounced save - nur UI aktualisieren, speichern bei Blur
    const updatedItems = inspection.items.map(item => 
      item.id === itemId ? { ...item, massnahme_text: text } : item
    );
    setInspection(prev => ({ ...prev, items: updatedItems }));
  };

  const saveMassnahmeText = async (itemId) => {
    const item = inspection.items.find(i => i.id === itemId);
    if (!item) return;
    
    try {
      await inspectionsApi.updateItem(id, itemId, {
        status: item.status,
        comment: item.comment || '',
        massnahme_typ: item.massnahme_typ,
        massnahme_text: item.massnahme_text || ''
      });
      
      // Synchronisiere Text in mittlere_hoehe_extras (falls "Mittlere Höhe" Checkliste)
      if (inspection.checklist_id === 'mittlere-hoehe-11-30m' && item.massnahme_typ) {
        await syncMassnahmeToExtras(itemId, item, item.massnahme_typ, item.massnahme_text || '');
      }
    } catch (error) {
      console.error('Error saving massnahme text:', error);
    }
  };

  const handlePhotoUpload = async (e) => {
    if (!selectedItem || !e.target.files?.[0]) return;
    
    setUploadingPhoto(true);
    try {
      await inspectionsApi.uploadPhoto(id, selectedItem.id, e.target.files[0]);
      await fetchInspection();
      // Update selectedItem with new data
      const updatedInspection = await inspectionsApi.getById(id);
      const updatedItem = updatedInspection.data.items.find(i => i.id === selectedItem.id);
      setSelectedItem(updatedItem);
    } catch (error) {
      console.error('Error uploading photo:', error);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handlePhotoDelete = async (photoIndex) => {
    if (!selectedItem) return;
    
    try {
      await inspectionsApi.deletePhoto(id, selectedItem.id, photoIndex);
      await fetchInspection();
      const updatedInspection = await inspectionsApi.getById(id);
      const updatedItem = updatedInspection.data.items.find(i => i.id === selectedItem.id);
      setSelectedItem(updatedItem);
    } catch (error) {
      console.error('Error deleting photo:', error);
    }
  };

  const openItemDialog = (item) => {
    setSelectedItem(item);
    setComment(item.comment || '');
  };

  const handleSaveSignature = async (role, signature, name, acknowledged) => {
    try {
      const response = await inspectionsApi.addSignature(id, { signature, name, role, acknowledged_defects: acknowledged });
      
      // Check for warning about critical defects
      if (response.data?.warning) {
        alert(response.data.warning);
      }
      
      await fetchInspection();
    } catch (error) {
      console.error('Error saving signature:', error);
      alert(error.response?.data?.detail || 'Fehler beim Speichern der Unterschrift');
    }
  };

  const handleDeleteSignature = async (role) => {
    try {
      await inspectionsApi.deleteSignature(id, role);
      await fetchInspection();
    } catch (error) {
      console.error('Error deleting signature:', error);
    }
  };

  const handleCompleteInspection = async () => {
    setCompleting(true);
    try {
      await inspectionsApi.complete(id);
      await fetchInspection();
      setShowCompleteDialog(false);
    } catch (error) {
      console.error('Error completing inspection:', error);
      alert(error.response?.data?.detail || 'Fehler beim Abschliessen der Abnahme');
    } finally {
      setCompleting(false);
    }
  };

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const response = await inspectionsApi.exportPDF(id);
      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `FireCheck_${inspection.property_name || inspection.property_address}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Fehler beim PDF-Export');
    } finally {
      setExporting(false);
    }
  };

  const handleSendEmail = async () => {
    setSendingEmail(true);
    try {
      const response = await inspectionsApi.sendEmail(id, {
        recipient_type: emailRecipient,
        custom_email: emailRecipient === 'custom' ? customEmail : null,
        include_pdf: includePdf
      });

      const results = response.data.results || [];
      const allSuccess = response.data.success;

      // Dialog schließen und detailliertes Ergebnis anzeigen
      setShowEmailDialog(false);

      if (allSuccess && results.length === 1) {
        // Einzel-Empfänger, alles ok -> einfacher Toast
        toast.success('E-Mail gesendet', {
          description: `An ${results[0].email}`
        });
      } else {
        // Mehrere Empfänger oder Fehler -> Detail-Dialog
        setEmailResultsDialog({ results, allSuccess });
      }
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error('E-Mail-Versand fehlgeschlagen', {
        description: error.response?.data?.detail || 'Bitte versuchen Sie es erneut'
      });
    } finally {
      setSendingEmail(false);
    }
  };

  const openEmailLog = async () => {
    setShowEmailLogDialog(true);
    setLoadingEmailLog(true);
    try {
      const response = await inspectionsApi.getEmailLog(id);
      setEmailLogs(response.data.logs || []);
    } catch (error) {
      console.error('Error loading email log:', error);
      toast.error('Logbuch konnte nicht geladen werden', {
        description: error.response?.data?.detail || 'Bitte versuchen Sie es erneut'
      });
      setEmailLogs([]);
    } finally {
      setLoadingEmailLog(false);
    }
  };

  // Übersetzt technische SMTP-Fehler in verständlichen Klartext
  const translateEmailError = (msg) => {
    if (!msg) return '';
    const m = String(msg);
    const low = m.toLowerCase();

    // Erfolgreich
    if (low.includes('erfolgreich') || low.includes('successfully')) return m;

    // Häufige SMTP-Fehlerbilder
    if (low.includes('unroutable') || low.includes('could not find any mailserver')) {
      return 'Empfänger-Domain existiert nicht (kein Mailserver / kein MX-Record). Bitte E-Mail-Adresse auf Tippfehler prüfen.';
    }
    if (low.includes('spam') || low.includes('550') && low.includes('reject')) {
      return 'Vom Empfänger als Spam abgelehnt. Absender-Domain muss zum SMTP-Server passen (SPF/DKIM/DMARC).';
    }
    if (low.includes('authentifizierung') || low.includes('authentication')) {
      return 'SMTP-Login fehlgeschlagen. Bitte Benutzer/Passwort in den E-Mail-Einstellungen prüfen.';
    }
    if (low.includes('adresse wurde abgelehnt') || low.includes('recipient') && low.includes('refused')) {
      return 'Empfänger-Adresse wurde vom Mailserver abgelehnt (ungültig oder blockiert).';
    }
    if (low.includes('ungültige e-mail')) {
      return 'Ungültiges E-Mail-Adressformat.';
    }
    if (low.includes('nicht konfiguriert')) {
      return 'E-Mail-Versand ist nicht konfiguriert. Bitte SMTP im Super-Admin einrichten.';
    }
    if (low.includes('timeout') || low.includes('timed out')) {
      return 'Zeitüberschreitung beim Verbinden zum SMTP-Server. Bitte später erneut versuchen.';
    }
    if (low.includes('connection refused') || low.includes('verbindung')) {
      return 'Keine Verbindung zum SMTP-Server möglich. Bitte Host/Port prüfen.';
    }

    // Fallback: technische Meldung
    return m;
  };

  // Group items by category
  const itemsByCategory = inspection?.items?.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {}) || {};

  const categories = Object.keys(itemsByCategory);
  
  // Calculate if inspection can be completed
  const pendingCount = inspection?.items?.filter(i => i.status === 'pending').length || 0;
  const failCount = inspection?.items?.filter(i => i.status === 'fail').length || 0;
  const hasInspectorSignature = !!inspection?.inspector_signature;
  const hasClientSignature = !!inspection?.client_signature;
  const hasAllSignatures = hasInspectorSignature && hasClientSignature;
  
  // Inspektion kann nur abgeschlossen werden, wenn:
  // 1. Alle Prüfpunkte bewertet sind (keine pending)
  // 2. Beide Unterschriften vorhanden sind
  // 3. Falls Mängel vorhanden: Betrieb hat die Mängel bestätigt (client_acknowledged_defects)
  const clientAcknowledged = inspection?.client_acknowledged_defects || failCount === 0;
  const canComplete = pendingCount === 0 && 
                      hasAllSignatures && 
                      clientAcknowledged &&
                      inspection?.status !== 'completed';
  const isCompleted = inspection?.status === 'completed';
  
  // Gründe warum nicht abgeschlossen werden kann
  const completionBlockers = [];
  if (pendingCount > 0) completionBlockers.push(`${pendingCount} Prüfpunkte offen`);
  if (!hasInspectorSignature) completionBlockers.push('Prüfer-Unterschrift fehlt');
  if (!hasClientSignature) completionBlockers.push('Betrieb-Unterschrift fehlt');
  if (failCount > 0 && !inspection?.client_acknowledged_defects) completionBlockers.push('Mängel nicht bestätigt');

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (!inspection) {
    return null;
  }

  const progress = calculateProgress(inspection.items);

  return (
    <Layout>
      <div className="space-y-6 animate-in fade-in slide-in-from-bottom duration-500" data-testid="inspection-detail">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="flex items-start gap-4">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate('/inspections')}
              className="shrink-0 mt-1"
              data-testid="back-button"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="font-heading font-bold text-2xl md:text-3xl">
                  {inspection.property_name || inspection.property_address}
                </h1>
                <Badge className={getInspectionStatusColor(inspection.status)}>
                  {getInspectionStatusLabel(inspection.status)}
                </Badge>
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  {inspection.property_address}
                </span>
                <span className="flex items-center gap-1">
                  <User className="h-4 w-4" />
                  {inspection.client_name}
                </span>
                <span className="flex items-center gap-1">
                  <Building2 className="h-4 w-4" />
                  {inspection.building_type}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  {formatDateTime(inspection.created_at)}
                </span>
                {inspection.property_contact_name && (
                  <span className="flex items-center gap-1">
                    <User className="h-4 w-4" />
                    {inspection.property_contact_name}
                  </span>
                )}
                {inspection.property_contact_email && (
                  <span className="flex items-center gap-1">
                    <Mail className="h-4 w-4" />
                    <a href={`mailto:${inspection.property_contact_email}`} className="hover:text-primary">
                      {inspection.property_contact_email}
                    </a>
                  </span>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex flex-col gap-2 ml-12 lg:ml-0">
            {!isCompleted && (
              <div className="flex gap-2">
                <Button 
                  variant={canComplete ? "default" : "outline"}
                  className={`gap-2 ${canComplete ? 'bg-green-500 hover:bg-green-600' : ''}`}
                  onClick={() => setShowCompleteDialog(true)}
                  disabled={!canComplete}
                  data-testid="complete-inspection-button"
                >
                  {canComplete ? (
                    <>
                      <CheckCheck className="h-4 w-4" />
                      Abnahme abschliessen
                    </>
                  ) : (
                    <>
                      <Lock className="h-4 w-4" />
                      Nicht bereit
                    </>
                  )}
                </Button>
                
                <Button 
                  variant="outline" 
                  className="gap-2" 
                  onClick={handleExportPDF}
                  disabled={exporting}
                  data-testid="export-pdf-button"
                >
                  {exporting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                  PDF Export
                </Button>
                
                <Button 
                  variant="outline" 
                  className="gap-2" 
                  onClick={() => setShowEmailDialog(true)}
                  disabled={!emailConfigured}
                  data-testid="send-email-button"
                >
                  <Mail className="h-4 w-4" />
                  Per E-Mail
                </Button>

                <Button
                  variant="outline"
                  className="gap-2"
                  onClick={openEmailLog}
                  data-testid="email-log-button"
                >
                  <History className="h-4 w-4" />
                  E-Mail-Logbuch
                </Button>
              </div>
            )}
            
            {/* Blockers Info */}
            {!isCompleted && !canComplete && completionBlockers.length > 0 && (
              <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-1">
                {completionBlockers.map((blocker, idx) => (
                  <span key={idx} className="flex items-center gap-1 text-amber-500">
                    <AlertCircle className="h-3 w-3" />
                    {blocker}
                  </span>
                ))}
              </div>
            )}
            
            {isCompleted && (
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  className="gap-2" 
                  onClick={handleExportPDF}
                  disabled={exporting}
                  data-testid="export-pdf-button"
                >
                  {exporting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                  PDF Export
                </Button>
                
                <Button 
                  variant="outline" 
                  className="gap-2" 
                  onClick={() => setShowEmailDialog(true)}
                  disabled={!emailConfigured}
                  data-testid="send-email-button-completed"
                >
                  <Mail className="h-4 w-4" />
                  Per E-Mail
                </Button>

                <Button
                  variant="outline"
                  className="gap-2"
                  onClick={openEmailLog}
                  data-testid="email-log-button-completed"
                >
                  <History className="h-4 w-4" />
                  E-Mail-Logbuch
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Progress Overview */}
        <Card className="border-border/50">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center gap-6">
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Fortschritt</span>
                  <span className="text-sm text-muted-foreground">{progress}%</span>
                </div>
                <Progress value={progress} className="h-2" />
              </div>
              
              <div className="flex gap-6">
                <div className="text-center">
                  <div className="flex items-center gap-1 text-green-500">
                    <CheckCircle2 className="h-5 w-5" />
                    <span className="font-heading font-bold text-2xl">{inspection.pass_count}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">OK</p>
                </div>
                <div className="text-center">
                  <div className="flex items-center gap-1 text-red-500">
                    <XCircle className="h-5 w-5" />
                    <span className="font-heading font-bold text-2xl">{inspection.fail_count}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">Mängel</p>
                </div>
                <div className="text-center">
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <MinusCircle className="h-5 w-5" />
                    <span className="font-heading font-bold text-2xl">
                      {inspection.total_count - inspection.pass_count - inspection.fail_count}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">Offen</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Erweiterte Daten für KAF/Mittlere Höhe - VOR den Prüfpunkten (Checkliste) */}
        {inspection.checklist_id === 'mittlere-hoehe-11-30m' && (
          <MittlereHoeheForm
            inspection={inspection}
            extras={inspection.mittlere_hoehe_extras}
            onSave={async (data) => {
              try {
                await inspectionsApi.updateMittlereHoeheExtras(inspection.id, data);
                await fetchInspection();
                toast.success('Erweiterte Daten gespeichert');
              } catch (error) {
                console.error('Error saving extras:', error);
                toast.error('Fehler beim Speichern');
              }
            }}
            disabled={isCompleted}
          />
        )}

        {/* Checklist Items */}
        <Tabs defaultValue={categories[0]} className="space-y-4">
          <TabsList className="flex flex-wrap h-auto gap-2 bg-transparent p-0">
            {categories.map((category) => {
              const items = itemsByCategory[category];
              const failCount = items.filter(i => i.status === 'fail').length;
              
              return (
                <TabsTrigger 
                  key={category} 
                  value={category}
                  className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2 rounded-lg border border-border/50"
                >
                  {category}
                  {failCount > 0 && (
                    <span className="ml-2 flex items-center gap-1 text-red-500">
                      <AlertTriangle className="h-3 w-3" />
                      {failCount}
                    </span>
                  )}
                </TabsTrigger>
              );
            })}
          </TabsList>

          {categories.map((category) => (
            <TabsContent key={category} value={category} className="space-y-3">
              {itemsByCategory[category].map((item, index) => (
                <Card 
                  key={item.id} 
                  className={`border-border/50 transition-all ${
                    item.status === 'fail' ? 'border-red-500/30' : ''
                  }`}
                  data-testid={`checklist-item-${item.id}`}
                >
                  <CardContent className="p-4 md:p-6">
                    {/* Main Row */}
                    <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                      {/* Item Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start gap-3">
                          <span className="text-sm text-muted-foreground font-mono shrink-0">
                            {String(index + 1).padStart(2, '0')}
                          </span>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium">{item.name}</h4>
                              {item.severity === 'critical' && (
                                <Badge className="bg-red-500/20 text-red-400 text-xs px-1.5 py-0">
                                  Sicherheitsrelevant
                                </Badge>
                              )}
                            </div>
                            {item.description && (
                              <p className="text-sm text-muted-foreground mt-0.5">{item.description}</p>
                            )}
                            {/* Info indicators */}
                            <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                              {(item.help_text || item.legal_reference) && (
                                <button
                                  onClick={() => setExpandedHelp(prev => ({...prev, [item.id]: !prev[item.id]}))}
                                  className="flex items-center gap-1 text-blue-400 hover:text-blue-300 transition-colors"
                                  data-testid={`help-toggle-${item.id}`}
                                >
                                  <HelpCircle className="h-3.5 w-3.5" />
                                  Hilfe & Gesetz
                                  {expandedHelp[item.id] ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                                </button>
                              )}
                              {item.comment && (
                                <span className="flex items-center gap-1">
                                  <MessageSquare className="h-3 w-3" />
                                  Kommentar
                                </span>
                              )}
                              {item.photos?.length > 0 && (
                                <span className="flex items-center gap-1">
                                  <ImageIcon className="h-3 w-3" />
                                  {item.photos.length} Foto(s)
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Status Buttons */}
                      <div className="flex items-center gap-2 ml-8 lg:ml-0">
                        <Button
                          variant={item.status === 'pass' ? 'default' : 'outline'}
                          size="sm"
                          className={`h-11 px-4 touch-target ${
                            item.status === 'pass' ? 'bg-green-500 hover:bg-green-600 text-white' : ''
                          }`}
                          onClick={() => handleStatusChange(item.id, 'pass')}
                          disabled={updating === item.id}
                          data-testid={`status-pass-${item.id}`}
                        >
                          {updating === item.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <CheckCircle2 className="h-4 w-4 mr-1" />
                              OK
                            </>
                          )}
                        </Button>
                        
                        <Button
                          variant={item.status === 'fail' ? 'default' : 'outline'}
                          size="sm"
                          className={`h-11 px-4 touch-target ${
                            item.status === 'fail' ? 'bg-red-500 hover:bg-red-600 text-white' : ''
                          }`}
                          onClick={() => handleStatusChange(item.id, 'fail')}
                          disabled={updating === item.id}
                          data-testid={`status-fail-${item.id}`}
                        >
                          {updating === item.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <XCircle className="h-4 w-4 mr-1" />
                              Mangel
                            </>
                          )}
                        </Button>
                        
                        <Button
                          variant={item.status === 'n/a' ? 'default' : 'outline'}
                          size="sm"
                          className={`h-11 px-4 touch-target ${
                            item.status === 'n/a' ? 'bg-orange-500 hover:bg-orange-600 text-white' : ''
                          }`}
                          onClick={() => handleStatusChange(item.id, 'n/a')}
                          disabled={updating === item.id}
                          data-testid={`status-na-${item.id}`}
                        >
                          N/A
                        </Button>

                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-11 w-11"
                          onClick={() => openItemDialog(item)}
                          data-testid={`open-item-dialog-${item.id}`}
                        >
                          <FileText className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    {/* Massnahmen-Auswahl bei Mängeln */}
                    {item.status === 'fail' && (
                      <div className="mt-4 ml-8 p-4 rounded-lg border animate-in fade-in slide-in-from-top-2 duration-200"
                        style={{
                          backgroundColor: getMassnahmeColor(item.massnahme_typ),
                          borderColor: getMassnahmeBorderColor(item.massnahme_typ)
                        }}
                        data-testid={`massnahme-section-${item.id}`}
                      >
                        <div className="flex flex-col md:flex-row md:items-center gap-4">
                          <div className="flex items-center gap-2">
                            <AlertTriangle className="h-4 w-4 text-red-500" />
                            <span className="font-medium text-sm">Massnahme erforderlich:</span>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {[
                              { key: 'sofort', label: 'Sofort', color: 'bg-red-100 hover:bg-red-200 border-red-300 text-red-800', frist: 'Unverzüglich' },
                              { key: 'kurzfristig', label: 'Kurzfristig', color: 'bg-yellow-100 hover:bg-yellow-200 border-yellow-300 text-yellow-800', frist: '3-6 Monate' },
                              { key: 'mittelfristig', label: 'Mittelfristig', color: 'bg-green-100 hover:bg-green-200 border-green-300 text-green-800', frist: '12-24 Monate' },
                              { key: 'langfristig', label: 'Langfristig', color: 'bg-blue-100 hover:bg-blue-200 border-blue-300 text-blue-800', frist: '2-5 Jahre' },
                            ].map((m) => (
                              <button
                                key={m.key}
                                onClick={() => handleMassnahmeChange(item.id, m.key)}
                                disabled={isCompleted}
                                className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-all ${
                                  item.massnahme_typ === m.key 
                                    ? `${m.color} ring-2 ring-offset-1 ring-current` 
                                    : 'bg-secondary/50 hover:bg-secondary border-border text-foreground'
                                }`}
                                data-testid={`massnahme-${m.key}-${item.id}`}
                              >
                                {m.label}
                                <span className="ml-1 opacity-70">({m.frist})</span>
                              </button>
                            ))}
                          </div>
                        </div>
                        {item.massnahme_typ && (
                          <div className="mt-3">
                            <Input
                              placeholder="Beschreibung der Massnahme..."
                              value={item.massnahme_text || ''}
                              onChange={(e) => handleMassnahmeTextChange(item.id, e.target.value)}
                              onBlur={() => saveMassnahmeText(item.id)}
                              disabled={isCompleted}
                              className="bg-white/50"
                              data-testid={`massnahme-text-${item.id}`}
                            />
                          </div>
                        )}
                      </div>
                    )}

                    {/* Expandable Help Section */}
                    {expandedHelp[item.id] && (item.help_text || item.legal_reference || item.criteria || item.example_images?.length > 0 || item.help_image) && (
                      <div className="mt-4 ml-8 p-4 bg-blue-500/5 border border-blue-500/20 rounded-lg space-y-3 animate-in fade-in slide-in-from-top-2 duration-200">
                        {/* Help Image for Laypersons */}
                        {item.help_image && (
                          <div>
                            <div className="flex items-center gap-2 text-sm font-medium text-purple-400 mb-2">
                              <ImageIcon className="h-4 w-4" />
                              Beispielbild
                            </div>
                            <div className="relative group inline-block">
                              <img 
                                src={item.help_image} 
                                alt={`Beispiel für ${item.name}`}
                                className="max-w-xs h-40 object-cover rounded-lg border border-border/50 cursor-pointer hover:opacity-90 transition-opacity"
                                onClick={() => setPreviewImage(item.help_image)}
                              />
                            </div>
                          </div>
                        )}
                        
                        {/* Example Images for Laypersons */}
                        {item.example_images && item.example_images.length > 0 && (
                          <div>
                            <div className="flex items-center gap-2 text-sm font-medium text-purple-400 mb-2">
                              <ImageIcon className="h-4 w-4" />
                              Beispielbilder
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                              {item.example_images.map((img, idx) => (
                                <div key={idx} className="relative group">
                                  <img 
                                    src={img.url} 
                                    alt={img.caption}
                                    className="w-full h-32 object-cover rounded-lg border border-border/50 cursor-pointer hover:opacity-90 transition-opacity"
                                    onClick={() => setPreviewImage(img.url)}
                                  />
                                  <div className="mt-1 text-xs text-muted-foreground">
                                    {img.caption}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {item.help_text && (
                          <div>
                            <div className="flex items-center gap-2 text-sm font-medium text-blue-400 mb-2">
                              <BookOpen className="h-4 w-4" />
                              Prüfanleitung
                            </div>
                            <div className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line bg-secondary/30 p-3 rounded-lg border border-border/30">
                              {item.help_text}
                            </div>
                          </div>
                        )}
                        
                        {item.legal_reference && (
                          <div>
                            <div className="flex items-center gap-2 text-sm font-medium text-amber-400 mb-1">
                              <Scale className="h-4 w-4" />
                              Gesetzliche Grundlage
                            </div>
                            <p className="text-sm text-muted-foreground">{item.legal_reference}</p>
                          </div>
                        )}
                        
                        {item.criteria && item.criteria.length > 0 && (
                          <div>
                            <div className="flex items-center gap-2 text-sm font-medium text-green-400 mb-2">
                              <ListChecks className="h-4 w-4" />
                              Prüfkriterien
                            </div>
                            <ul className="space-y-1">
                              {item.criteria.map((criterion, idx) => (
                                <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                                  <span className="text-green-500 mt-1">•</span>
                                  {criterion}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </TabsContent>
          ))}
        </Tabs>

        {/* Signatures Section - am Ende der Seite */}
        <Card className="border-border/50">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <PenLine className="h-5 w-5 text-primary" />
              Unterschriften
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Beide Unterschriften werden für den PDF-Export benötigt
            </p>
          </CardHeader>
          <CardContent>
            <SignatureSection
              inspection={inspection}
              onSaveSignature={handleSaveSignature}
              onDeleteSignature={handleDeleteSignature}
              disabled={isCompleted}
            />
            
            {/* Signature status indicators */}
            <div className="flex gap-4 mt-4 text-sm">
              <div className={`flex items-center gap-2 ${inspection.inspector_signature ? 'text-green-500' : 'text-muted-foreground'}`}>
                {inspection.inspector_signature ? <CheckCircle2 className="h-4 w-4" /> : <MinusCircle className="h-4 w-4" />}
                Prüfer {inspection.inspector_signature ? 'unterschrieben' : 'fehlt'}
              </div>
              <div className={`flex items-center gap-2 ${inspection.client_signature ? 'text-green-500' : 'text-muted-foreground'}`}>
                {inspection.client_signature ? <CheckCircle2 className="h-4 w-4" /> : <MinusCircle className="h-4 w-4" />}
                Betrieb {inspection.client_signature ? 'unterschrieben' : 'fehlt'}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Notes */}
        {inspection.notes && (
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                Bemerkungen
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">{inspection.notes}</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Item Detail Dialog */}
      <Dialog open={!!selectedItem} onOpenChange={() => setSelectedItem(null)}>
        <DialogContent className="max-w-lg fixed top-4 left-[50%] translate-x-[-50%] translate-y-0 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedItem?.name}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Current Status */}
            <div>
              <p className="text-sm text-muted-foreground mb-2">Status</p>
              <Badge className={getStatusColor(selectedItem?.status)}>
                {getStatusLabel(selectedItem?.status)}
              </Badge>
            </div>

            {/* Comment */}
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Kommentar</p>
              <Textarea
                placeholder="Kommentar hinzufügen..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={3}
                data-testid="item-comment-textarea"
              />
              <Button 
                size="sm" 
                onClick={handleCommentSave}
                disabled={updating === selectedItem?.id}
                data-testid="save-comment-button"
              >
                {updating === selectedItem?.id ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : null}
                Kommentar speichern
              </Button>
            </div>

            {/* Photos */}
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Fotos</p>
              
              {selectedItem?.photos?.length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                  {selectedItem.photos.map((photo, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={photo}
                        alt={`Foto ${index + 1}`}
                        className="w-full h-24 object-cover rounded-lg cursor-pointer"
                        onClick={() => setPreviewImage(photo)}
                      />
                      <Button
                        variant="destructive"
                        size="icon"
                        className="absolute top-1 right-1 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => handlePhotoDelete(index)}
                        data-testid={`delete-photo-${index}`}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                onChange={handlePhotoUpload}
                className="hidden"
              />
              
              <Button
                variant="outline"
                className="w-full gap-2"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingPhoto}
                data-testid="upload-photo-button"
              >
                {uploadingPhoto ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Camera className="h-4 w-4" />
                )}
                Foto hinzufügen
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog open={!!previewImage} onOpenChange={() => setPreviewImage(null)}>
        <DialogContent className="max-w-3xl p-2 fixed top-4 left-[50%] translate-x-[-50%] translate-y-0 max-h-[90vh] overflow-y-auto">
          <DialogHeader className="sr-only">
            <DialogTitle>Bildvorschau</DialogTitle>
          </DialogHeader>
          <img
            src={previewImage}
            alt="Vorschau"
            className="w-full h-auto rounded-lg"
          />
        </DialogContent>
      </Dialog>

      {/* Complete Inspection Dialog */}
      <AlertDialog open={showCompleteDialog} onOpenChange={setShowCompleteDialog}>
        <AlertDialogContent className="fixed top-4 left-[50%] translate-x-[-50%] translate-y-0 max-h-[90vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <CheckCheck className="h-5 w-5 text-green-500" />
              Abnahme abschliessen?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                Sind Sie sicher, dass Sie diese Brandschutz-Abnahme abschliessen möchten?
              </p>
              <div className="p-4 rounded-lg bg-secondary/50 space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Prüfpunkte gesamt:</span>
                  <span className="font-medium">{inspection?.total_count}</span>
                </div>
                <div className="flex justify-between text-sm text-green-500">
                  <span>OK:</span>
                  <span className="font-medium">{inspection?.pass_count}</span>
                </div>
                <div className="flex justify-between text-sm text-red-500">
                  <span>Mängel:</span>
                  <span className="font-medium">{inspection?.fail_count}</span>
                </div>
              </div>
              {inspection?.fail_count > 0 && (
                <p className="text-amber-500 text-sm flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  Es wurden {inspection?.fail_count} Mängel festgestellt.
                </p>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleCompleteInspection}
              disabled={completing}
              className="bg-green-500 hover:bg-green-600"
            >
              {completing ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <CheckCheck className="h-4 w-4 mr-2" />
              )}
              Abnahme abschliessen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Email Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="max-w-md fixed top-4 left-[50%] translate-x-[-50%] translate-y-0 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-primary" />
              Bericht per E-Mail senden
            </DialogTitle>
            <DialogDescription>
              Senden Sie den Brandschutz-Abnahmebericht an die gewünschten Empfänger.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Absender Info */}
            <div className="p-3 rounded-lg bg-muted/50 text-sm">
              <p className="text-muted-foreground">
                <strong>Absender:</strong> FireCheck Rapport (report@fire-check.ch)
              </p>
            </div>
            
            {/* Recipient Selection */}
            {(() => {
              // Get client email - check both standard and KAF locations
              const extras = inspection?.mittlere_hoehe_extras || {};
              const objektDaten = extras.objekt_daten || {};
              const verteiler = extras.verteiler || {};
              const sicherheitsbeauftragter = extras.sicherheitsbeauftragter || {};
              
              // Priority: property_contact_email > objekt_daten.eigentuemer_email > verteiler.eigentuemer_email
              const clientEmail = inspection?.property_contact_email || 
                objektDaten.eigentuemer_email ||
                verteiler.eigentuemer_email || '';
              const hasClientEmail = !!clientEmail;

              // Verteiler-Empfänger (KAF)
              const verteilerEmails = [];
              if (verteiler.eigentuemer && verteiler.eigentuemer_email) verteilerEmails.push(verteiler.eigentuemer_email);
              if (verteiler.gemeinde && verteiler.gemeinde_email) verteilerEmails.push(verteiler.gemeinde_email);
              if (verteiler.kanton && verteiler.kanton_email) verteilerEmails.push(verteiler.kanton_email);
              const hasVerteiler = verteilerEmails.length > 0;
              
              return (
                <div className="space-y-3">
                  <Label className="text-sm font-medium">Empfänger auswählen</Label>
                  <RadioGroup value={emailRecipient} onValueChange={setEmailRecipient}>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="inspector" id="r-inspector" data-testid="email-recipient-inspector" />
                      <Label htmlFor="r-inspector" className="font-normal cursor-pointer">
                        An mich (Prüfer)
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="client" id="r-client" disabled={!hasClientEmail} data-testid="email-recipient-client" />
                      <Label htmlFor="r-client" className={`font-normal cursor-pointer ${!hasClientEmail ? 'text-muted-foreground' : ''}`}>
                        An Eigentümer/Betrieb ({clientEmail || 'Keine E-Mail hinterlegt'})
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="both" id="r-both" disabled={!hasClientEmail} data-testid="email-recipient-both" />
                      <Label htmlFor="r-both" className={`font-normal cursor-pointer ${!hasClientEmail ? 'text-muted-foreground' : ''}`}>
                        An mich + Eigentümer
                      </Label>
                    </div>
                    {hasVerteiler && (
                      <div className="flex items-start space-x-2">
                        <RadioGroupItem value="verteiler" id="r-verteiler" className="mt-1" data-testid="email-recipient-verteiler" />
                        <div className="flex-1">
                          <Label htmlFor="r-verteiler" className="font-normal cursor-pointer">
                            An Verteiler ({verteilerEmails.length} Empfänger)
                          </Label>
                          <p className="text-xs text-muted-foreground mt-0.5 break-all">
                            {verteilerEmails.join(', ')}
                          </p>
                        </div>
                      </div>
                    )}
                    {hasVerteiler && (
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="all" id="r-all" data-testid="email-recipient-all" />
                        <Label htmlFor="r-all" className="font-normal cursor-pointer">
                          An alle (Prüfer + Eigentümer + Verteiler)
                        </Label>
                      </div>
                    )}
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="custom" id="r-custom" data-testid="email-recipient-custom" />
                      <Label htmlFor="r-custom" className="font-normal cursor-pointer">
                        Andere E-Mail-Adresse
                      </Label>
                    </div>
                  </RadioGroup>
                  
                  {emailRecipient === 'custom' && (
                    <Input
                      type="email"
                      placeholder="email@beispiel.ch"
                      value={customEmail}
                      onChange={(e) => setCustomEmail(e.target.value)}
                      className="mt-2"
                      data-testid="email-custom-input"
                    />
                  )}
                </div>
              );
            })()}
            
            {/* Include PDF Option */}
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="include-pdf" 
                checked={includePdf} 
                onCheckedChange={setIncludePdf} 
              />
              <Label htmlFor="include-pdf" className="font-normal cursor-pointer">
                PDF-Bericht als Anhang
              </Label>
            </div>
          </div>
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowEmailDialog(false)}>
              Abbrechen
            </Button>
            <Button 
              onClick={handleSendEmail}
              disabled={sendingEmail || (emailRecipient === 'custom' && !customEmail)}
              className="gap-2"
            >
              {sendingEmail ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              Senden
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* E-Mail-Logbuch Dialog */}
      <Dialog open={showEmailLogDialog} onOpenChange={setShowEmailLogDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] flex flex-col" data-testid="email-log-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              E-Mail-Logbuch
            </DialogTitle>
            <DialogDescription>
              Alle E-Mails, die für diese Inspektion versendet wurden (manuell und automatisch).
            </DialogDescription>
          </DialogHeader>

          <div className="overflow-y-auto flex-1 -mx-6 px-6">
            {loadingEmailLog ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : emailLogs.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Mail className="h-10 w-10 mx-auto mb-2 opacity-30" />
                <p>Noch keine E-Mails versendet.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {emailLogs.map((log) => (
                  <div
                    key={log.id}
                    className={`border rounded-lg p-3 text-sm ${
                      log.success
                        ? 'border-green-200 bg-green-50/50 dark:bg-green-900/10'
                        : 'border-red-200 bg-red-50/50 dark:bg-red-900/10'
                    }`}
                    data-testid="email-log-entry"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {log.success ? (
                            <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
                          ) : (
                            <XCircle className="h-4 w-4 text-red-600 flex-shrink-0" />
                          )}
                          <span className="font-medium truncate">{log.recipient}</span>
                          <Badge variant="outline" className="text-xs">
                            {log.recipient_type === 'inspector' && 'Prüfer'}
                            {log.recipient_type === 'client' && 'Eigentümer'}
                            {log.recipient_type === 'betrieb' && 'Betrieb'}
                            {log.recipient_type === 'pruefer' && 'Prüfer'}
                          </Badge>
                          {log.category === 'massnahme_reminder' && (
                            <Badge variant="secondary" className="text-xs">Maßnahmen-Erinnerung</Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground truncate" title={log.subject}>
                          {log.subject}
                        </p>
                        {log.error && (
                          <p className="text-xs text-red-600 mt-1 break-words">
                            Fehler: {translateEmailError(log.error)}
                          </p>
                        )}
                        <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
                          <span>{formatDateTime(log.sent_at)}</span>
                          <span>·</span>
                          <span>von {log.sent_by_email}</span>
                          {log.include_pdf && (
                            <>
                              <span>·</span>
                              <span className="flex items-center gap-1"><FileText className="h-3 w-3" /> PDF</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <div className="text-xs text-muted-foreground mr-auto self-center">
              {emailLogs.length} Eintrag{emailLogs.length === 1 ? '' : 'e'}
            </div>
            <Button variant="outline" onClick={() => setShowEmailLogDialog(false)}>
              Schließen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Versand-Ergebnis Dialog (pro Empfänger) */}
      <Dialog open={!!emailResultsDialog} onOpenChange={(open) => !open && setEmailResultsDialog(null)}>
        <DialogContent className="max-w-2xl" data-testid="email-results-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {emailResultsDialog?.allSuccess ? (
                <>
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  E-Mails gesendet
                </>
              ) : (
                <>
                  <AlertCircle className="h-5 w-5 text-red-600" />
                  Versand nur teilweise erfolgreich
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {emailResultsDialog?.allSuccess
                ? `Alle ${emailResultsDialog?.results?.length || 0} E-Mail(s) wurden erfolgreich versendet.`
                : 'Nachfolgend sehen Sie das Ergebnis pro Empfänger. Details zu Fehlern finden Sie auch im E-Mail-Logbuch.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {emailResultsDialog?.results?.map((r, idx) => (
              <div
                key={idx}
                className={`flex items-start gap-3 p-3 rounded-lg border ${
                  r.success
                    ? 'border-green-200 bg-green-50 dark:bg-green-950/20'
                    : 'border-red-200 bg-red-50 dark:bg-red-950/20'
                }`}
                data-testid={`email-result-${r.success ? 'success' : 'failed'}`}
              >
                {r.success ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm break-all">{r.email}</div>
                  <div className={`text-xs mt-1 ${r.success ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`}>
                    {translateEmailError(r.message)}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <DialogFooter>
            {!emailResultsDialog?.allSuccess && (
              <Button
                variant="outline"
                onClick={() => {
                  setEmailResultsDialog(null);
                  openEmailLog();
                }}
                data-testid="open-email-log-from-results"
              >
                <History className="h-4 w-4 mr-2" />
                Logbuch öffnen
              </Button>
            )}
            <Button onClick={() => setEmailResultsDialog(null)} data-testid="close-email-results">
              Schließen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
